import React, { ChangeEvent, useContext, useMemo, useState, useEffect, useCallback } from "react";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import { OwViewOptionsPanel } from "../../shared/OwViewOptionsPanel/OwViewOptionsPanel";
import IViewOptions from "../../../models/IViewOptions";
import ControlTypeEnum from "../../../models/ControlTypeEnum";
import SupplierService from "../../../services/SupplierService";
import IPackingListViewOptions from "../../../models/IPackingListViewOptions";
import IOrder_ItemUI from "../../../models/IOrder_ItemUI";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IQtyAssigned from "../../../models/IQtyAssigned";
import ILot from "../../../models/ILot";
import IPacking_List from "../../../models/IPacking_List";
import { usePackingListViewColumns } from "../../../hooks/packing_list/usePackingListViewColumns";
import { PackingListDetailHeader } from "./PackingListDetailHeader/PackingListDetailHeader";
import { IOption } from "../../../models/IOption";
import IUpdatePackingListRow from "../../../models/IUpdatePackingListRow";
import IUpdatePackingListSourceRow from "../../../models/IUpdatePackingListSourceRow";
import _ from "lodash";
import ILotUI from "../../../models/ILotUI";
import IUpdatePackingListDTO from "../../../models/DTO/IUpdatePackingListDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUpdatePackingListLockDTO from "../../../models/DTO/IUpdatePackingListLockDTO";
import ShipmentPackageSidebar from "../../shipments/ShipmentPackageSidebar/ShipmentPackageSidebar";
import IShipment_Group from "../../../models/IShipment_Group";
import IGetPackingListDTO from "../../../models/DTO/IGetPackingListDTO";
import { PackingListDetailTopRow } from "./PackingListDetailTopRow/PackingListDetailTopRow";
import IOrder from "../../../models/IOrder";
import { PackingListDetailBillToShipToRow } from "./PackingListDetailBillToShipToRow/PackingListDetailBillToShipToRow";
import UserService from "../../../services/UserService";
import { PackingListDetailDetailRow } from "./PackingListDetailDetailRow/PackingListDetailDetailRow";
import OwTable from "../../shared/OwTable/OwTable";
import { PackingListDetailLotsDetailView } from "./PackingListDetailLotsDetailView/PackingListDetailLotsDetailView";
import INewOrder_Item_Lot_Map from "../../../models/INewOrder_Item_Lot_Map";
import UIService from "../../../services/UIService";
import { usePackingListDetailViewColumns } from "../../../hooks/packing_list/usePackingListDetailViewColumns";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IOnGetPackingListDTO from "../../../models/DTO/IOnGetPackingListDTO";
import IOnUpdatePackingListDTO from "../../../models/DTO/IOnUpdatePackingListDTO";
import IOnUpdatePackingListLockDTO from "../../../models/DTO/IOnUpdatePackingListLockDTO";
import { useMatch } from "react-router-dom";
import { PackingListService } from "../../../services/PackingListService";
import "./PackingListDetailFn.scss";
import { usePackingListViewOptionsUI } from "../../../hooks/packing_list/usePackingListViewOptionsUI";
import { usePackingListViewOptions } from "../../../hooks/packing_list/usePackingListViewOptions";
import IUpdateSupplierSettingsDTO from "../../../models/DTO/IUpdateSupplierSettingsDTO";
import IOnUpdateSupplierSettingsDTO from "../../../models/DTO/IOnUpdateSupplierSettingsDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";

type Props = {};

export const PackingListDetailFn = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const match = useMatch("/packing_lists/:packing_list_guid");

    const crumbs: IBreadcrumbs[] = useMemo(() => {
        return [
            { title: "Packing Lists", url: "/packing_lists" },
            { title: "Detail", url: "/packing_lists/:guid" },
        ];
    }, []);

    const supplierSettings = SupplierService.getSupplierSettings(supplierModel);

    const [isLocked, setIsLocked] = useState(false);

    const [viewOptions, handleViewOptionChange, setViewOptions] = usePackingListViewOptions(supplierSettings);

    const packingListTemplates: IOption[] = [
        { value: "STANDARD", label: "8.5x11" },
        { value: "MINIMAL", label: "4x6" },
    ];

    const [isSaveActive, setIsSaveActive] = useState(false);
    const [isFinalizeActive, setIsFinalizeActive] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);

    const [pageSize, setPageSize] = useState<"STANDARD" | "MINIMAL">("STANDARD");

    const [catalogItems, setCatalogItems] = useState<ICatalog_Item[]>([]);
    const [orderItems, setOrderItems] = useState<IOrder_ItemUI[]>([]);
    const [qtyAssigned, setQtyAssigned] = useState<IQtyAssigned>({});

    const [packingList, setPackingList] = useState<IPacking_List>({} as IPacking_List);

    const [totalOrders, setTotalOrders] = useState<number>(0);
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [firstOrder, setFirstOrder] = useState<IOrder | null>(null);

    const [hasAlternateSources, setHasAlternateSources] = useState<boolean>(false);

    const onGetPackingList = useCallback(
        (data: IOnGetPackingListDTO) => {
            const processedPackingList = PackingListService.processPackingList(data, qtyAssigned);

            document.title = `Orderwerks >> Packing List >> ${data.packingList.name}`;

            let _hasUnsavedChanges = false;

            if (isInitialData && !data.packingList.is_locked) {
                _hasUnsavedChanges = true;
            }

            const orderDetails = getOrders(processedPackingList.packingList);

            let _hasAlternateSources = false;

            for (const orderItems of data.packingList.Order_Items || []) {
                if (orderItems.Catalog_Item?.has_alternate_sourcing) {
                    _hasAlternateSources = true;
                    break;
                }
            }

            setHasAlternateSources(_hasAlternateSources);
            setHasUnsavedChanges(_hasUnsavedChanges);
            setPackingList(processedPackingList.packingList);
            setCatalogItems(processedPackingList.catalogItems);
            setOrderItems(processedPackingList.orderItemsSorted);
            setIsDataFetching(false);
            setIsInitialData(false);
            setQtyAssigned(processedPackingList.qtyAssigned);
            setFirstOrder(orderDetails.firstOrder);
            setOrders(orderDetails.orders);

            uiContext.setActivityIndicator(false);
            // setFoundLots(_foundLots);
            // setSelectedLots(_selectedLots);

            const _view = {
                ...viewOptions,
            };

            _view.showOrder = !(data.packingList.Orders?.length === 1);

            setViewOptions(_view);
            setTotalOrders(data.packingList.Orders?.length || 0);
        },
        [hasUnsavedChanges, isDataFetching, isFinalizeActive, isInitialData]
    );

    const onUpdatePackingList = (data: IOnUpdatePackingListDTO) => {
        setIsSaveActive(false);
        setHasUnsavedChanges(false);

        getPackingList();
    };

    const onUpdatePackingListLock = (data: IOnUpdatePackingListLockDTO) => {
        setIsFinalizeActive(false);
        setHasUnsavedChanges(false);

        getPackingList();
    };

    const onUpdateSupplierSettings = (data: IOnUpdateSupplierSettingsDTO) => {
        const message: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_updated_settings",
        };

        uiContext.notifyFn(null, message);
    };

    // We have to do this so we get the latest values inside of the socket callback functions.
    useEffect(() => {
        const sockets: IUiSocketSetup[] = [
            { endpoint: EndpointsEnum.OrderOnGetPackingList, fn: onGetPackingList },
            { endpoint: EndpointsEnum.OrderOnUpdatePackingList, fn: onUpdatePackingList },
            { endpoint: EndpointsEnum.OrderOnUpdatePackingListLock, fn: onUpdatePackingListLock },
            { endpoint: EndpointsEnum.SupplierOnUpdateSupplierSettings, fn: onUpdateSupplierSettings },
        ];

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        if (isInitialData) {
            uiContext.setActivityIndicator(true);

            getPackingList();
        }

        return () => {
            for (const _socket of sockets) {
                // Ensure using the same function reference for removal
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };

        // ***** KEY CHANGE: Add the memoized callbacks here *****
    }, [hasUnsavedChanges]); // Add ALL callbacks used/defined that useEffect depends on

    const optionsPanelViewOptions = usePackingListViewOptionsUI(viewOptions, supplierModel);

    const saveViewOptions = () => {
        const payload: IUpdateSupplierSettingsDTO = {
            supplierGuid: supplierModel.guid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            // @ts-expect-error
            settings: {
                simple_packing_list: {
                    show_phone: viewOptions.showPhone,
                    show_email: viewOptions.showEmail,
                    show_order_qty: viewOptions.showOrderQty,
                    show_ship_qty: viewOptions.showShipQty,
                    show_dropshipment: viewOptions.showDropshipment,
                    show_order_desc: viewOptions.showOrderDescription,
                    show_sku: viewOptions.showSku,
                    show_upc: viewOptions.showUpc,
                    hide_sku_col: viewOptions.hideSkuCol,
                    show_ship_to_phone: viewOptions.showShipToPhone,
                    show_bill_to_phone: viewOptions.showBillToPhone,
                    show_order_user: viewOptions.showOrderUser,
                    show_row_bin: viewOptions.showItemLocation,
                    show_uom: viewOptions.showUom,
                    inline_logo_name: viewOptions.inlineLogoName,
                    show_bill_to: viewOptions.showBillTo,
                    show_item_photo: viewOptions.showItemPhoto,
                    hide_business_name: viewOptions.hideBusinessName,
                    show_dimensions: viewOptions.showDimensions,
                    show_item_location: viewOptions.showItemLocation,
                    show_packing_list_name: viewOptions.showPackingListName,
                },
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
    };

    const getOrders = (_packingList?: IPacking_List): { orders: IOrder[]; firstOrder: IOrder } => {
        const __packingList = _packingList || packingList;

        const orders: IOrder[] = __packingList && __packingList.Orders ? __packingList.Orders : [{} as IOrder];
        const firstOrder = orders[0];

        return { orders, firstOrder };
    };

    const handleQtyAssignedChange = (e: any, row: IOrder_ItemUI) => {
        let value = e.target.value;

        const _qtyAssigned = {
            ...qtyAssigned,
        };

        if (value < 0) {
            value = 0;
        }

        if (value > row.qty) {
            value = row.qty;
        }

        _qtyAssigned[row.guid].value = Number(value);

        console.log("handleQtyAssignedChange", value);

        setQtyAssigned(_qtyAssigned);
        setHasUnsavedChanges(true);
    };

    const handleCartonChange = (e: any, row: IOrder_ItemUI) => {
        const value = e.target.value;
        const name = e.target.name;

        const _orderItems = {
            ...orderItems,
        };

        for (const orderItem of _orderItems) {
            if (orderItem.guid === row.guid) {
                if (orderItem.Packing_List_Order_Item_Map) {
                    orderItem.Packing_List_Order_Item_Map.cartons = value;
                }
            }
        }

        setOrderItems(_orderItems);
        setHasUnsavedChanges(true);
    };

    const hasLotsChange = (e: ChangeEvent<any>, row: IOrder_ItemUI) => {
        const _orderItems = {
            ...orderItems,
        };

        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        for (const orderItem of _orderItems) {
            if (orderItem.guid === row.guid) {
                orderItem.Packing_List_Order_Item_Map!.has_lots = value;
                orderItem.use_lots = value;
            }
        }

        setOrderItems(_orderItems);
        setHasUnsavedChanges(true);
    };

    const viewColumns = usePackingListViewColumns(
        viewOptions,
        packingList,
        supplierModel,
        qtyAssigned,
        handleQtyAssignedChange,
        hasLotsChange,
        handleCartonChange,
        hasAlternateSources
    );

    const savePackingList = () => {
        const orderItemKeys = Object.keys(qtyAssigned);

        const rows: IUpdatePackingListRow[] = [];
        const sourceRows: IUpdatePackingListSourceRow[] = [];

        for (const orderItemKey of orderItemKeys) {
            const orderItemRow = qtyAssigned[orderItemKey];

            const row: IUpdatePackingListRow = {
                orderItemGuid: orderItemKey,
                qtyAssigned: Number(orderItemRow.value),
            };

            const sourcesKeys = Object.keys(orderItemRow.sources);

            let rowQty = 0;

            if (sourcesKeys.length) {
                for (const sourceKey of sourcesKeys) {
                    const sourceDetail = orderItemRow.sources[sourceKey];

                    const sourceRow: IUpdatePackingListSourceRow = {
                        catalogItemGuid: sourceKey,
                        qtyPacked: Number(sourceDetail.value),
                        parentCatalogItemId: sourceDetail.parent_catalog_item_id,
                        orderId: sourceDetail.order_id,
                    };

                    sourceRows.push(sourceRow);

                    rowQty += sourceRow.qtyPacked;
                }

                row.qtyAssigned = rowQty;
            }

            rows.push(row);
        }

        if (orderItems) {
            for (const orderItem of orderItems) {
                for (const row of rows) {
                    if (row.orderItemGuid === orderItem.guid) {
                        if (orderItem.Packing_List_Order_Item_Map) {
                            if (orderItem.Packing_List_Order_Item_Map.cartons) {
                                row.cartons = orderItem.Packing_List_Order_Item_Map.cartons;
                            }
                        }

                        if (orderItem.Lots) {
                            const lotsProcessed = orderItem.Lots.filter((lot) => {
                                return !_.isNil(lot.qty);
                            });

                            row.lots = lotsProcessed.map((lot: ILotUI) => {
                                // TODO delete flag?

                                return {
                                    qty: lot.qty,
                                    lot_id: lot.id,
                                    deleteFlag: lot.deleteFlag,
                                };
                            });
                        }

                        if (orderItem.use_lots) {
                            row.use_lots = orderItem.use_lots;
                        }
                    }
                }
            }
        }

        const payload: IUpdatePackingListDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            packingListGuid: packingList.guid,
            rows,
            sourceRows,
        };

        socket.emit(EndpointsEnum.OrderUpdatePackingList, payload);

        setIsSaveActive(true);
        setIsInitialData(false);
    };

    const updatePackingListLock = (isLocked = true) => {
        const payload: IUpdatePackingListLockDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            packingListGuid: packingList.guid,
            isLocked,
        };

        socket.emit(EndpointsEnum.OrderUpdatePackingListLock, payload);

        setIsFinalizeActive(true);
    };

    const getPackingList = () => {
        if (match?.params?.packing_list_guid) {
            const payload: IGetPackingListDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                packingListGuid: match.params.packing_list_guid,
            };

            socket.emit(EndpointsEnum.OrderGetPackingList, payload);

            setIsDataFetching(true);
        }
    };

    const getDate = (orderModel: IOrder, property: "confirmed_date" | "estimated_shipping_at" | "created_at" | "submit_date"): string => {
        if (orderModel) {
            if (orderModel[property]) {
                return UserService.parseDate(orderModel[property]);
            } else {
                return "";
            }
        } else {
            return "";
        }
    };

    const getOrderDate = () => {
        let orderDate = "";

        if (orders.length > 2) {
            orderDate = "Mixed";
        } else {
            if (firstOrder) {
                if (firstOrder.confirmed_date) {
                    orderDate = getDate(firstOrder, "confirmed_date");
                } else if (firstOrder.submit_date) {
                    orderDate = getDate(firstOrder, "submit_date");
                } else {
                    orderDate = getDate(firstOrder, "created_at");
                }
            }
        }

        return orderDate;
    };

    const getCompiledData = (): {
        orderNumsArr: string[];
        poNumsArr: string[];
        notes: string;
        shipping_instructions: string;
        orderDescription: string;
    } => {
        const orderNumsArr: string[] = [];
        const poNumsArr: string[] = [];
        let notes = "";
        let shipping_instructions = "";
        let orderDescription = "";

        _.each(orders, (order) => {
            orderNumsArr.push(order.name!);

            if (order.po_no) {
                poNumsArr.push(order.po_no);
            }

            if (!_.isNil(order.note)) {
                notes += order.note + "\n";
            }

            if (!_.isNil(order.shipping_instructions)) {
                if (orders.length > 1) {
                    shipping_instructions += `${order.name!}: ${order.shipping_instructions}`;
                } else {
                    shipping_instructions += `${order.shipping_instructions}`;
                }
            }

            if (!_.isNil(order.description)) {
                if (orders.length > 1) {
                    orderDescription += `${order.name!}: ${order.description}`;
                } else {
                    orderDescription += `${order.description}`;
                }
            }
        });

        return { orderNumsArr, poNumsArr, notes, shipping_instructions, orderDescription };
    };

    const shipmentProcessed = () => {};

    const onShipmentUpdate = () => {};

    const cancel = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        getPackingList();
    };

    const showShipmentSidebar = () => {
        const component = (
            <ShipmentPackageSidebar
                shipmentGroup={{} as IShipment_Group}
                packingList={packingList}
                cancel={cancel}
                notifyFn={uiContext.notifyFn}
                socket={socket}
                processedFn={shipmentProcessed}
                onUpdateFn={onShipmentUpdate}
                userModel={userModel}
                supplierModel={supplierModel}
            />
        );

        uiContext.setSidebarComponent(component);

        uiContext.toggleSidebar(true);
    };

    const selectPageSize = (pageSize: IOption) => {
        setPageSize(pageSize.value as "STANDARD" | "MINIMAL");
    };

    const rowHasDetailView = (row: IOrder_ItemUI): boolean => {
        if (row.Catalog_Item?.Product_Sources) {
            return true;
        }

        if (row.use_lots) {
            return true;
        }

        return false;
    };

    const handleQtyChange = (e: ChangeEvent<any>, lot: ILotUI, parentRowIndex: number) => {
        const _orderItems = {
            ...orderItems,
        };

        const orderItem = _orderItems[parentRowIndex];

        if (orderItem && orderItem.Lots) {
            for (const itemLot of orderItem.Lots) {
                if (itemLot.guid === lot.guid) {
                    itemLot.qty = Number(e.target.value || 0);
                }
            }
        }

        const _packingList = {
            ...packingList,
        };
        const orderItemLotMaps = _packingList.Order_Item_Lot_Maps;

        if (_packingList) {
            if (orderItemLotMaps) {
                for (const orderItemLotMap of orderItemLotMaps) {
                    if (orderItemLotMap.lot_id === lot.id && orderItemLotMap.order_item_id === orderItem.id) {
                        console.log("handleQtyChange: ", orderItemLotMap, e.target.value);
                        if (!_.isNil(e.target.value)) {
                            orderItemLotMap.qty = Number(e.target.value || 0);
                        }
                    }
                }
            }
        }

        packingList.Order_Item_Lot_Maps = orderItemLotMaps;

        setOrderItems(_orderItems);
        setPackingList(_packingList);
        setHasUnsavedChanges(true);
    };

    const addLot = (row: IOrder_ItemUI, selectedLots: { [key: string]: ILot | null }) => {
        const lot = selectedLots[row.guid];

        if (!lot) {
            return null;
        }

        lot.qty = 0;

        const _orderItems = {
            ...orderItems,
        };

        const orderItemLotMap = packingList.Order_Item_Lot_Maps;

        for (const orderItem of _orderItems) {
            if (orderItem.guid === row.guid) {
                if (!orderItem.Lots) {
                    orderItem.Lots = [];
                }

                const newLotMap: INewOrder_Item_Lot_Map = {
                    lot_id: lot.id,
                    order_item_id: orderItem.id,
                    order_id: orderItem.order_id!,
                    catalog_item_id: orderItem.catalog_item_id,
                    packing_list_id: packingList.id,
                    qty: 0,
                };

                // @ts-expect-error
                orderItemLotMap.push(newLotMap);

                if (orderItem.Lots.length) {
                    let lotExists = false;

                    for (const existingLot of orderItem.Lots) {
                        if (lot.guid === existingLot.guid) {
                            lotExists = true;
                        }
                    }

                    if (!lotExists) {
                        orderItem.Lots.push(lot);
                    }
                } else {
                    orderItem.Lots.push(lot);
                }
                // orderItem.Lot = lot;
                // orderItem.Packing_List_Order_Item_Map.
            }
        }

        const _packingList = {
            ...packingList,
        };

        _packingList.Order_Item_Lot_Maps = orderItemLotMap;

        setOrderItems(orderItems);
        setPackingList(_packingList);
        setHasUnsavedChanges(true);
    };

    const deleteLot = (lot: ILotUI, parentRowIndex: number) => {
        const _orderItems = {
            ...orderItems,
        };

        const orderItem = _orderItems[parentRowIndex];

        if (orderItem && orderItem.Lots) {
            for (const itemLot of orderItem.Lots) {
                if (itemLot.guid === lot.guid) {
                    itemLot.deleteFlag = true;
                }
            }
        }

        setOrderItems(_orderItems);
        setHasUnsavedChanges(true);
    };

    const handlePickedChange = (e: any, row: ICatalog_Item, rowIndex: number, isDetailView?: boolean, parentRowIndex?: number) => {
        console.log("handlePickedChange", e, row, rowIndex, isDetailView, parentRowIndex);

        if (!_.isNil(parentRowIndex)) {
            const _qtyAssigned = {
                ...qtyAssigned,
            };

            const parentOrderItem = orderItems[parentRowIndex];

            if (!_qtyAssigned[parentOrderItem.guid].sources[row.guid]) {
                _qtyAssigned[parentOrderItem.guid].sources[row.guid] = {
                    qty_picked: 0,
                    value: 0,
                    parent_catalog_item_id: parentOrderItem.Catalog_Item!.id,
                    order_id: parentOrderItem!.order_id!,
                };
            }

            let value = Number(e.target.value);

            if (value < 0) {
                value = 0;
            }

            _qtyAssigned[parentOrderItem.guid].sources[row.guid].value = value;

            const keys = Object.keys(_qtyAssigned[parentOrderItem.guid].sources);

            let qtyAssignedTotal = 0;

            for (const key of keys) {
                qtyAssignedTotal += Number(_qtyAssigned[parentOrderItem.guid].sources[key].value);
            }

            _qtyAssigned[parentOrderItem.guid].value = qtyAssignedTotal;

            setQtyAssigned(_qtyAssigned);
            setHasUnsavedChanges(true);
        }
    };

    const lotsDetailView = (row: IOrder_ItemUI, parentRowIndex: number) => {
        return (
            <PackingListDetailLotsDetailView
                packingList={packingList}
                row={row}
                parentRowIndex={parentRowIndex}
                orderItems={orderItems}
                handleQtyChange={handleQtyChange}
                addLot={addLot}
                deleteLot={deleteLot}
            />
        );
    };

    let tableTheme = "";

    if (!UIService.isMainViewLight()) {
        tableTheme = "table-dark";
    }

    const detailViewRowOverride = (row: IOrder_ItemUI) => {
        return !!row.Packing_List_Order_Item_Map?.has_lots;
    };

    const copyPickedQtyToShipQty = () => {
        const _qtyAssigned = {
            ...qtyAssigned,
        };

        // Track remaining qty_picked per catalog_item_id for duplicate SKUs
        const remainingBySkuId: { [key: number]: number } = {};

        const keys = Object.keys(_qtyAssigned);
        for (const key of keys) {
            const qtyAssignedItem = _qtyAssigned[key];
            const orderItem = orderItems.find((item) => item.guid === key);

            if (orderItem?.catalog_item_id && qtyAssignedItem.qty_picked > 0) {
                const skuId = orderItem.catalog_item_id;

                // Initialize remaining qty for this SKU if not set
                if (remainingBySkuId[skuId] === undefined) {
                    remainingBySkuId[skuId] = qtyAssignedItem.qty_picked;
                }

                // Assign up to the row's qty, but not more than remaining picked
                const maxForRow = orderItem.qty ?? qtyAssignedItem.qty_picked;
                const toAssign = Math.min(maxForRow, remainingBySkuId[skuId]);

                qtyAssignedItem.value = toAssign;
                remainingBySkuId[skuId] -= toAssign;
            }
        }

        setQtyAssigned(_qtyAssigned);
        setHasUnsavedChanges(true);
    };

    const detailViewCols = usePackingListDetailViewColumns(packingList, orderItems, qtyAssigned, handlePickedChange);

    return (
        <div className={"PackingListDetailFn mb-5 " + pageSize}>
            <OwViewOptionsPanel
                viewOptions={optionsPanelViewOptions}
                onChangeFn={handleViewOptionChange}
                showSaveBtn={true}
                saveFn={saveViewOptions}
            />

            <Breadcrumbs crumbs={crumbs} />

            <div className={"mb-5"}>
                {!firstOrder ? (
                    isInitialData ? null : (
                        <div className={"mt-5"}>Error. No Order - Did you delete it?</div>
                    )
                ) : (
                    <>
                        <PackingListDetailHeader
                            packingList={packingList}
                            savePackingList={savePackingList}
                            updatePackingListLock={updatePackingListLock}
                            isSaveActive={isSaveActive}
                            isFinalizeActive={isFinalizeActive}
                            hasUnsavedChanges={hasUnsavedChanges}
                            showShipmentSidebar={showShipmentSidebar}
                            selectPageSize={selectPageSize}
                            packingListTemplates={packingListTemplates}
                            copyPickedQtyToShipQty={copyPickedQtyToShipQty}
                        />
                        <PackingListDetailTopRow packingList={packingList} view={viewOptions} pageSize={pageSize} firstOrder={firstOrder} />
                        <PackingListDetailBillToShipToRow
                            packingList={packingList}
                            view={viewOptions}
                            pageSize={pageSize}
                            firstOrder={firstOrder}
                            orders={orders}
                            totalOrders={totalOrders}
                            getCompiledData={getCompiledData}
                            getDate={getDate}
                            getOrderDate={getOrderDate}
                        />
                        <PackingListDetailDetailRow
                            packingList={packingList}
                            view={viewOptions}
                            pageSize={pageSize}
                            firstOrder={firstOrder}
                            orders={orders}
                            totalOrders={totalOrders}
                            getCompiledData={getCompiledData}
                            getDate={getDate}
                            getOrderDate={getOrderDate}
                        />
                        <div className={"items-row"}>
                            <OwTable
                                encloseRowsInTbody={true}
                                noDefaultWrappingClassName={true}
                                tableClassName={"table table-bordered " + tableTheme}
                                overrideTableClassName={true}
                                columns={viewColumns}
                                dataSet={orderItems}
                                userModel={userModel}
                                supplierModel={supplierModel}
                                dataFetch={isDataFetching}
                                initialData={isInitialData}
                                tableConfig={{ hasDetailView: true, detailViewFullWidth: false, tableHover: true }}
                                detailViewColumns={detailViewCols}
                                detailViewDataSetProp={"Catalog_Item"}
                                detailViewDataSetSubProp={"Product_Sources"}
                                detailViewInitialData={false}
                                detailViewDataFetch={false}
                                detailViewRowOverride={detailViewRowOverride}
                                detailViewComponent={lotsDetailView}
                                detailViewAlwaysVisible={!!supplierModel.has_lots}
                                rowDetailViewFn={rowHasDetailView}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
